import type { IconType } from "react-icons";
import {
	FiBarChart2,
	FiBook,
	FiBox,
	FiDollarSign,
	FiFileText,
	FiShield,
	FiTrendingUp
} from "react-icons/fi";
import { categorizeContentType, type ContentCategoryId } from "../Content/contentCategories";
import { parseApiDate } from "../../Helper/dates";

/** One queued production change, as served by GET /api/deployment/production/overview. */
export type ProductionPublishItem = {
	id: string;
	contentId: string | null;
	/** Schema key of the content type : used to build the editor deep-link. */
	contentType: string | null;
	slug: string | null;
	/** Human title of the content, resolved server-side (falls back to slug/id). */
	title: string | null;
	language: string;
	action: string;
	message: string | null;
	author: string | null;
	error: string | null;
	attemptCount: number;
	status: string;
	/** Deployed public path(s) : used to build the preview link. */
	pagePaths: string[];
	createdAt: string | null;
	updatedAt: string | null;
};

/** One content type's pending production changes (items newest first, capped server-side). */
export type ProductionPublishGroup = {
	contentTypeId: string;
	title: string;
	/** Feed-driven type (e.g. taux de change) : published automatically on every feed update. */
	auto: boolean;
	/** REAL total for the group; `items` is capped at 25. */
	count: number;
	lastQueuedAtUtc: string | null;
	items: ProductionPublishItem[];
};

export type ProductionOverview = {
	pendingTotal: number;
	failedTotal: number;
	failed: ProductionPublishItem[];
	groups: ProductionPublishGroup[];
};

export const EMPTY_OVERVIEW: ProductionOverview = {
	pendingTotal: 0,
	failedTotal: 0,
	failed: [],
	groups: []
};

export const normalizePublishItem = (raw: any): ProductionPublishItem => ({
	id: String(raw?.id ?? ""),
	contentId: raw?.contentId ?? null,
	contentType: raw?.contentType ?? null,
	slug: raw?.slug ?? null,
	title: raw?.title ?? null,
	language: String(raw?.language ?? "fr"),
	action: String(raw?.action ?? "updated"),
	message: raw?.message ?? null,
	author: raw?.author ?? null,
	error: raw?.error ?? null,
	attemptCount: Number(raw?.attemptCount ?? 0),
	status: String(raw?.status ?? "pending"),
	pagePaths: Array.isArray(raw?.pagePaths) ? raw.pagePaths.map((p: any) => String(p)) : [],
	createdAt: raw?.createdAt ?? null,
	updatedAt: raw?.updatedAt ?? null
});

export const normalizePublishGroup = (raw: any): ProductionPublishGroup => ({
	contentTypeId: String(raw?.contentTypeId ?? ""),
	title: String(raw?.title ?? raw?.contentTypeId ?? ""),
	auto: raw?.auto === true,
	count: Number(raw?.count ?? 0),
	lastQueuedAtUtc: raw?.lastQueuedAtUtc ?? null,
	items: Array.isArray(raw?.items) ? raw.items.map(normalizePublishItem) : []
});

export const normalizeOverview = (raw: any): ProductionOverview => ({
	pendingTotal: Number(raw?.pendingTotal ?? 0),
	failedTotal: Number(raw?.failedTotal ?? 0),
	failed: Array.isArray(raw?.failed) ? raw.failed.map(normalizePublishItem) : [],
	groups: Array.isArray(raw?.groups) ? raw.groups.map(normalizePublishGroup) : []
});

/* ------------------------------------------------------------------ */
/*  Preview sync status                                               */
/* ------------------------------------------------------------------ */

export type PreviewSyncState = "starting" | "disabled" | "synced" | "syncing" | "error";

export type PreviewStatus = {
	state: PreviewSyncState;
	pendingCount: number;
	failedCount: number;
	lastSucceeded: number;
	lastFailed: number;
	lastSyncedAtUtc: string | null;
	updatedAtUtc: string | null;
	lastError: string | null;
};

/** Enum order server-side: Starting, Disabled, Synced, Syncing, Error. */
const PREVIEW_STATE_BY_INDEX: PreviewSyncState[] = ["starting", "disabled", "synced", "syncing", "error"];

/**
 * The API serializes the state enum as a string (Newtonsoft StringEnumConverter → "Synced"),
 * but we defend against camelCase strings and raw enum numbers so a serializer change can
 * never silently break the pill.
 */
export const normalizePreviewState = (raw: unknown): PreviewSyncState => {
	if (typeof raw === "number" && raw >= 0 && raw < PREVIEW_STATE_BY_INDEX.length) {
		return PREVIEW_STATE_BY_INDEX[raw];
	}
	const s = String(raw ?? "").trim().toLowerCase();
	if (s === "starting" || s === "disabled" || s === "synced" || s === "syncing" || s === "error") return s;
	const n = Number(s);
	if (Number.isInteger(n) && n >= 0 && n < PREVIEW_STATE_BY_INDEX.length) return PREVIEW_STATE_BY_INDEX[n];
	return "starting";
};

export const normalizePreviewStatus = (raw: any): PreviewStatus => ({
	state: normalizePreviewState(raw?.state),
	pendingCount: Number(raw?.pendingCount ?? 0),
	failedCount: Number(raw?.failedCount ?? 0),
	lastSucceeded: Number(raw?.lastSucceeded ?? 0),
	lastFailed: Number(raw?.lastFailed ?? 0),
	lastSyncedAtUtc: raw?.lastSyncedAtUtc ?? null,
	updatedAtUtc: raw?.updatedAtUtc ?? null,
	lastError: raw?.lastError ?? null
});

/* ------------------------------------------------------------------ */
/*  French formatting helpers                                          */
/* ------------------------------------------------------------------ */

/** French label for a queue action ("modified"/"updated" → « modifié », etc.). */
export const actionLabelFr = (action?: string | null): string => {
	const a = (action ?? "").trim().toLowerCase();
	if (a === "added" || a === "created" || a === "new") return "ajouté";
	if (a === "deleted" || a === "removed") return "supprimé";
	return "modifié";
};

/** True pour une ligne « supprimé » : son bouton devient « Retirer de la production ». */
export const isDeletionAction = (action?: string | null): boolean => actionLabelFr(action) === "supprimé";

/** Chip classes for an action, aligned with the palette used across the admin. */
export const actionChipClass = (action?: string | null): string => {
	const label = actionLabelFr(action);
	if (label === "ajouté") return "border-emerald-200 bg-emerald-50 text-emerald-700";
	if (label === "supprimé") return "border-rose-200 bg-rose-50 text-rose-700";
	return "border-sky-200 bg-sky-50 text-sky-700";
};

/** Compact French relative time with second-level granularity (« il y a 12 s »). */
export const relativeTimeFr = (iso?: string | null): string => {
	const date = parseApiDate(iso);
	if (!date) return "";
	const s = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
	if (s < 60) return `il y a ${s} s`;
	const m = Math.floor(s / 60);
	if (m < 60) return `il y a ${m} min`;
	const h = Math.floor(m / 60);
	if (h < 24) return `il y a ${h} h`;
	const d = Math.floor(h / 24);
	return d < 7 ? `il y a ${d} j` : date.toLocaleDateString("fr-FR");
};

/** Full localized timestamp for tooltips. */
export const exactDateFr = (iso?: string | null): string => {
	const date = parseApiDate(iso);
	return date ? date.toLocaleString("fr-FR") : "";
};

/** « 3 modifications », « 1 échec »… */
export const pluralFr = (count: number, singular: string, plural?: string): string =>
	`${count} ${count > 1 ? plural ?? `${singular}s` : singular}`;

/* ------------------------------------------------------------------ */
/*  Jetons de déploiement périmétrés (miroir de ContentDeployGate)      */
/* ------------------------------------------------------------------ */

/** Singletons de configuration globale : les déployer touche TOUT le site, aucun périmètre
 *  départemental n'a de sens ; la clé technique est exigée (même liste que le gate serveur). */
const TECHNICAL_SINGLETONS = new Set([
	"layout",
	"layout-header",
	"layout-footer",
	"languages",
	"contact-info",
	"site-settings",
	"sitemap",
	"settings"
]);

/**
 * Le jeton exigé pour mettre CE type en production : `content:<type>:promote` pour les contenus
 * départementaux (pages : clé de catalogue `basic-page`), la clé technique pour les singletons
 * de configuration et l'inconnu (fermé par défaut). Miroir STRICT du ContentDeployGate serveur ;
 * les détenteurs de `feature:deployment:write` passent de toute façon (le serveur applique un OU,
 * et l'expansion de la clé technique ne donne PAS les jetons content : GatedButton doit donc
 * recevoir le jeton que l'utilisateur détient réellement, voir l'appelant).
 */
export const promoteTokenForType = (contentTypeId?: string | null): string => {
	const key = (contentTypeId ?? "").trim().toLowerCase();
	if (!key || TECHNICAL_SINGLETONS.has(key)) return "feature:deployment:write";
	if (key === "page") return "content:basic-page:promote";
	return `content:${key}:promote`;
};

/** Lock message from a 409 response ({ message, lockedBy }). */
export const lockMessageFrom = (err: any): string => {
	const data = err?.response?.data;
	if (typeof data?.message === "string" && data.message.trim()) return data.message;
	const holder = typeof data?.lockedBy === "string" && data.lockedBy.trim() ? data.lockedBy : "un autre éditeur";
	return `Une publication est déjà en cours (${holder}).`;
};

/* ------------------------------------------------------------------ */
/*  Per-content-type icon                                              */
/* ------------------------------------------------------------------ */

/** Same domain buckets as the Content browser (categorizeContentType), one icon per bucket. */
const CATEGORY_ICONS: Record<ContentCategoryId, IconType> = {
	editorial: FiFileText,
	rates: FiDollarSign,
	securities: FiTrendingUp,
	stats: FiBarChart2,
	reports: FiBook,
	institutions: FiShield,
	other: FiBox
};

/** Icon for a content type, derived from the Content browser's domain categorization. */
export const iconForContentType = (title?: string | null, key?: string | null): IconType =>
	CATEGORY_ICONS[categorizeContentType(title, key)] ?? FiBox;
